export type ResponderType = 'aiBot' | 'agent' | 'user';

export interface ActionDef {
  type: 'link' | 'button' | 'postback';
  ctaType?: string;
  text: string;
  url?: string;
  payload?: any;
  userMessage?: string;
}

export interface Pet {
  id: string;
  name: string;
  imageUrls?: string[];
  breedType?: string;
}

export interface UserActionDef {
  defaultAction?: ActionDef;
  showSeparator?: boolean;
  onClick?: (data: UserActionDef | undefined) => void;
}

export enum UserActionType {
  CONTACT_CARE_TEAM = 'CONTACT_CARE_TEAM',
  CONNECT_TO_AGENT = 'CONNECT_TO_AGENT',
  VIEW_ORDER = 'VIEW_ORDER',
  ORDER_HELP = 'ORDER_HELP',
  VIEW_AUTOSHIP = 'VIEW_AUTOSHIP',
  AUTOSHIP = 'AUTOSHIP',
  VIEW_ACCOUNT = 'VIEW_ACCOUNT',
  MANAGE_ACCOUNT = 'MANAGE_ACCOUNT',
}

export enum ChatWrapupSheetPageType {
  EMAIL = 'email',
  COMMENTS = 'comments'
}

export enum ChatWrapupSheetActionType {
  CLOSE_CHAT = 'CloseChat',
  START_NEW_CHAT = 'StartNewChat',
  EMAIL_CHAT_TRANSCRIPT = 'EMAIL_CHAT_TRANSCRIPT',
  CHAT_FEEDBACK_POSITIVE = 'CHAT_FEEDBACK_POSITIVE',
  CHAT_FEEDBACK_NEGATIVE = 'CHAT_FEEDBACK_NEGATIVE'
}

export type AdditionalInfoType = 'default' | 'pharmacy' | 'returns' | 'account';
export interface AdditionalInfoItem {
  key?: string | React.ReactNode;
  value?: string | React.ReactNode;
}
export interface AdditionalInfoDef {
  type: AdditionalInfoType;
  items: AdditionalInfoItem[];
}
export interface PaymentTagDef {
  paymentMethod: string;
  label?: string;
}
export interface TagDef {
  label?: string | React.ReactNode;
  Icon: string;
}

export const itemTagMap: Record<string, string> = {
  'PRESCRIPTION ITEM': 'prescription',
  PRESCRIPTION: 'prescription',
  RX: 'prescription',
  DIET: 'diet',
  FROZEN: 'frozen',
  CWAV: 'chewy',
  'CHEWY+': 'chewyPlus',
  PERSONALIZED: 'personalized',
  LIVEFEED: 'liveFeed'
};

export interface ProductCardData {
  imageUrl?: string;
  itemId?: string;
  quantity?: number;
  itemTags?: string[];
  name?: string;
  status?: string;
  prescriptionDetails?: PrescriptionDetails[];
}

export type FileCardStatus = 'UPLOADING' | 'UPLOADED' | 'FAILED';

export interface FileCardData {
  attachmentId?: string;
  status?: FileCardStatus;
  error?: string;
  fileType?: string;
  url?: string;
  byteSize?: number;
  fileName?: string;
}
export interface ReturnCardData extends CoreCardData {
  status?: string;
  returnNumber?: string;
  totalReturns?: number;
  description?: string;
}

export interface OrderReturnData extends OrderCardData {
  widgets?: ReturnCardData[];
}

export interface PackageCardData extends CoreCardData {
  status?: string;
  packageNumber?: string;
  totalPackages?: number;
  description?: string;
}
export interface OrderPackageData extends OrderCardData {
  widgets?: PackageCardData[];
}

export const MessageTypes = {
  general: 'general',
  typing: 'typing',
  structured: 'structured',
  ordercard: 'ordercard',
  ordercards: 'ordercards',
  ordercardmultiitem: 'ordercardmultiitem',
  ordercardpackage: 'ordercardpackage',
  ordercardpackageselector: 'ordercardpackageselector',
  ordercardselector: 'ordercardselector',
  productcard: 'productcard',
  productcardselector: 'productcardselector',
  thumbnailcard: 'thumbnailcard',
  thumbnailcardselector: 'thumbnailcardselector',
  filecard: 'filecard',
  text: 'text',
  orders: 'orders',
  transferring: 'transferring',
  transferred: 'transferred',
  agentjoined: 'agentjoined'
} as const;

export type MessageType = keyof typeof MessageTypes;

export interface DateStatus {
  /** Formatted date, e.g. "Wed, Dec 3" */
  label?: string;
  /** Status prefix, e.g. "Arrives by" */
  status?: string;
  /** YYYY-MM-DD formatted date */
  date?: string;
}
export interface CoreCardData {
  title?: string;
  orderId?: string;
  tags?: TagDef[];
  cardType?: string;
  dateStatus?: DateStatus;
  orderStatus?: string;
  date?: string;
  items?: ProductCardData[];
  additionalMetaData?: AdditionalInfoItem[];
  payments?: PaymentTagDef[];
}

export interface OrderCardData extends CoreCardData {
  subscriptionFrequency?: string;
  subscriptionId?: string;
  subscriptionName?: string;
  actions?: ActionDef[];
}

export const AUTOSHIP_CARD_TYPES = [
  'AUTOSHIP',
  'AUTOSHIP_LIST_COMPONENT',
  'AUTOSHIP_LIST_COMPONENT_MULTI_ITEM'
];

/** Order/autoship list cardTypes only (not prescription or other LIST_COMPONENT variants). */
export const AUTOSHIP_ORDER_LIST_COMPONENT_CARD_TYPES = [
  'ORDER_LIST_COMPONENT',
  'AUTOSHIP_LIST_COMPONENT',
  'AUTOSHIP_LIST_COMPONENT_MULTI_ITEM'
];

export interface PetProfileAction {
  type: 'link' | 'button' | 'postback';
  text: string;
  url?: string;
}

export interface PetProfileDefaultAction {
  type: 'Postback' | 'Link';
  payload: {
    action: string;
    petProfileId: string;
  };
}

export interface PetProfileCardPayload {
  cardType?: 'PET_PROFILE';
  id?: string;
  name?: string;
  breedName?: string;
  weightInPounds?: number;
  status?: string;
  birthday?: string;
  imageUrls?: string[];
  breedType?: string;
  defaultAction?: PetProfileDefaultAction;
  actions?: PetProfileAction[];
}

export interface PrescriptionDetails {
  quantityRemaining?: string | null;
  prescribedAmount?: string | null;
  refillsRemaining?: string;
  expirationTime?: string | null;
  rxContextualMessage?: string;
  sendTo?: string;
}

export interface PrescriptionItem {
  imageUrl?: string;
  itemId?: string;
  name?: string;
  prescriptionDetails?: PrescriptionDetails[];
  itemTags?: string[];
}

export interface PrescriptionCardPayload {
  cardType?: string;
  prescriptionId?: string;
  items?: PrescriptionItem[];
  actions?: ActionDef[];
  additionalMetaData?: AdditionalInfoItem[];
  prescriptionState?: string;
}

export type MessageWidget = {
  headingText: string;
  messageState: boolean;
  persist: boolean;
  subText: string;
  trigger: boolean;
};

export type WidgetContent = {
  widgetType: string;
  widgets: MessageWidget[];
};

export interface ChatMessage<TPayload = Record<string, any>> {
  id: string;
  conversationId: string;
  type: MessageType;
  requestId?: string;
  responseType: ResponderType;
  /** ISO date string */
  responseTimestamp: string;
  agentName?: string; // For agent messages
  content?: string | WidgetContent[];
  message?: string; // For simple text messages
  payload?: TPayload;
  isLoading?: boolean;
  source?: 'LIVE' | 'TRANSCRIPT';
  error?: string;
}

export type WidgetProps<TPayload = Record<string, any>> = {
  /**
   * Chat message
   */
  message?: ChatMessage<TPayload>;
  isSelectable?: boolean;
  isSelected?: boolean;
  isUnselected?: boolean;
  disabled?: boolean;
  onSelect?: () => void;
  data?: TPayload[];
  stackDateStatus?: boolean;
  onAction?: (action: { type: string; payload?: any }) => void;
  /**
   * Layout direction for the cards
   */
  direction?: 'horizontal' | 'vertical';
  /**
   * Additional CSS class name
   */
  className?: string;
  /**
   * Callback function to request scrolling to a selected item
   */
  requestScroll?: RequestScrollCallback;
}

export type WidgetComponent<TPayload = Record<string, any>> = React.ComponentType<WidgetProps<TPayload>>;

export type WidgetPayloadComponent<TPayload = Record<string, any>> = React.ComponentType<{
  payload: TPayload | TPayload[];
}>;

/**
 * Shape of a scroll request to be enqueued in `useScrollHandler`.
 */
export type ScrollRequest = {
  /** The element to scroll to */
  element: HTMLDivElement;
  /** ISO date string of the message timestamp */
  timestamp: string;
  /** Higher numbers are higher priority */
  priority?: number;
  /** Time to pause at this element before scrolling to the next in queue */
  timeout?: number;
  /** Callback to indicate when request is complete */
  onRequestComplete?: () => void;
  /** Callback to indicate when scroll is complete */
  onScrollComplete?: () => void;
  /** Callback to indicate when all scrolls are complete */
  onScrollAllComplete?: () => void;
};

export type RequestScrollCallback = (scrollRequest: ScrollRequest) => void;

export type WrapupFeedbackThankYouState = {
  sentiment: 'positive' | 'negative' | null;
} | null;

export type Env = 'prd' | 'dev' | 'stg' | 'qat';

export type UserAttributes = {
  userId: string;
  fullName: string;
  emailAddress: string;
  pets?: Array<{
    id: string;
    name: string;
  }>;
};
